import { useState } from "react";
import { FaInstagram } from "react-icons/fa";
import Button from "../controls/buttons";

interface IMenuButton {
  toggleMenu: React.MouseEventHandler<HTMLButtonElement>;
  showMenu: boolean;
}

type Link = {
  label: string;
  href: string;
};

const links = [
  {
    label: `About`,
    href: `#/about`,
  },
  {
    label: `Hospitality`,
    href: `#Hospitality`,
  },
  {
    label: `Commercial`,
    href: `#Commercial`,
  },
  {
    label: `Residential`,
    href: `#Residential`,
  },
];

const secondaryLinks = [
  {
    label: `Contact us`,
    href: `#contact`,
  },
];

const MenuButton = ({ toggleMenu, showMenu }: IMenuButton) => (
  <button
    type="button"
    aria-controls="mobile-menu"
    aria-expanded={showMenu}
    onClick={toggleMenu}
    className="p-2 text-h-gold"
  >
    <span className="sr-only">Open menu</span>
    {showMenu ? (
      <svg
        className="h-6 w-6"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
        width={24}
        height={24}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    ) : (
      <svg
        className="h-6 w-6"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
        width={24}
        height={24}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      </svg>
    )}
  </button>
);

const MobileMenu = () => (
  <div className="md:hidden bg-h-navy">
    <div className="px-5 pt-2 pb-3 space-y-1 sm:px-3">
      {links.map((link: Link) => (
        <a
          href={link.href}
          className="font-glacial text-h-gold uppercase tracking-[0.2em] font-normal text-xl block px-5 py-4"
          key={link.label}
        >
          {link.label}
        </a>
      ))}
    </div>
    <div className="pt-4 pb-3 border-t border-h-gold">
      <div className="px-2 space-y-1">
        {secondaryLinks.map((link: Link) => (
          <a
            key={`mobile-${link.label}`}
            href={link.href}
            className="font-glacial text-h-gold uppercase tracking-[0.2em] font-normal text-xl block px-5 py-4"
          >
            {link.label}
          </a>
        ))}
        <a
          href="https://www.instagram.com/hprojectsdubai"
          target="_blank"
          rel="noreferrer"
          aria-label="H Projects on Instagram"
          className="text-h-gold flex justify-center py-4"
        >
          <FaInstagram size={28} />
        </a>
      </div>
    </div>
  </div>
);

const Navigation = () => {
  const [showMenu, setShowMenu] = useState(false);
  const toggleMenu = () => setShowMenu(!showMenu);

  return (
    <nav className="bg-h-navy">
      {/* Mobile: centered H masthead with hamburger top-right */}
      <div className="md:hidden relative">
        <div className="absolute top-3 right-2 z-10">
          <MenuButton showMenu={showMenu} toggleMenu={toggleMenu} />
        </div>
        <div className="flex flex-col items-center overflow-hidden pt-1">
          <img
            src="images/brand/logo-h.png"
            alt="H Projects logo"
            className="h-64 w-64 object-cover select-none mix-blend-lighten -mt-12 -mb-16"
          />
          <p className="font-glacial font-medium text-h-gold select-none text-2xl tracking-[0.25em] text-center">
            H PROJECTS
          </p>
          <p className="text-h-gold select-none text-[0.65rem] font-normal tracking-[0.3em] mt-1 pb-4 text-center">
            COLLABORATION CREATES
          </p>
        </div>
      </div>
      {showMenu ? <MobileMenu /> : null}

      {/* Desktop: giant centered lockup, links row beneath */}
      <div className="hidden md:flex flex-col items-center overflow-hidden">
        <img
          src="images/brand/logo-h.png"
          alt="H Projects logo"
          className="h-[40rem] w-[40rem] object-cover select-none mix-blend-lighten -mt-24 -mb-28"
        />
        <div className="text-center">
          <p className="font-glacial font-medium text-h-gold select-none text-4xl xl:text-5xl tracking-[0.3em]">
            H PROJECTS
          </p>
          <p className="text-h-gold select-none text-sm xl:text-base font-normal tracking-[0.35em] mt-2">
            COLLABORATION CREATES
          </p>
        </div>
        <div className="flex items-center gap-1 xl:gap-4 py-6">
          {links.map((link: Link) => (
            <a
              key={link.label}
              href={link.href}
              className="text-h-gold font-bold hover:opacity-70 px-3 xl:px-4 py-2 rounded-md text-lg xl:text-xl select-none"
            >
              {link.label}
            </a>
          ))}
          <a
            href="https://www.instagram.com/hprojectsdubai"
            target="_blank"
            rel="noreferrer"
            aria-label="H Projects on Instagram"
            className="text-h-gold hover:opacity-70"
          >
            <FaInstagram size={28} />
          </a>
          <Button
            primary
            modifier="bclassNameorder-0 ml-2 select-none"
            link="#contact"
          >
            Contact us
          </Button>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
